import * as _ from "lodash";
import { Grid } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CustomButton, {
  CustomButtonVariant,
} from "../../../components/CustomButton/CustomButton";
import { useDistributor } from "../../../contexts/distributor/DistributorContext";
import { useIocContext } from "../../../contexts/ioc/IocContext";
import { ISupplyService } from "../../../modules/supply/models/ISupplyService";
import { Types } from "../../../ioc/types";
import AppError from "../../../utils/appError";
import useDialogAlert from "../../../hooks/DialogAlert";
import {
  ContractDTO,
  IQueryBalanceProps,
} from "../../../modules/supply/models/ContractDTO";
import MultipleSelectCheckbox from "../../../components/MultipleSelectCheckbox";
import { convertToOptionsList } from "../../../utils";
import MultipleSelectDistributors from "../../../components/MultipleSelectDistributors/MultipleSelectDistributors";
import SelectSubsidiaries from "../../../components/SelectSubsidiary/SelectSubsidiary";
import MonthYearPicker from "../../../components/MonthYearPicker/MonthYearPicker";
import { formatMinMaxDate } from "../../../utils/dateFormatter";
import { startOfMonth, subMonths } from "date-fns";

interface IBalanceFilterTableProps {
  onButtonChange: (query: IQueryBalanceProps) => void;
  onButtonClearFilter: (isClear: boolean) => void;
}

const BalanceFilterTableSection: React.FC<IBalanceFilterTableProps> = ({
  onButtonChange,
  onButtonClearFilter,
}) => {
  const { selectedDistributorsStoraged, subsidiaries } = useDistributor();
  const { serviceContainer } = useIocContext();
  const { snackbar } = useDialogAlert();
  const [codeDistributor, setCodeDistributor] = useState<string | null>(null);
  const [codePeriod, setCodePeriod] = useState<string | null>(null);
  const [codeContract, setCodeContract] = useState<string[] | string>([]);
  const [codeSubsidiary, setCodeSubsidiary] = useState<string | undefined>(
    undefined
  );
  const [codeMods, setCodeMods] = useState<string[] | string>([]);
  const [codeProducts, setCodeProducts] = useState<string[] | string>([]);
  const [period, setPeriod] = useState<Array<Date | null>>([]);
  const [contracts, setContracts] = useState<ContractDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const supplyService = serviceContainer.get<ISupplyService>(
    Types.Supply.ISupplyService
  );

  const getPeriodData = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        const response = await supplyService.getAllPeriodByCompany(id);

        const previousMonth = startOfMonth(subMonths(new Date(2022, 0, 1), 1));

        const dataWithDates = response.map((item) => {
          const [month, year] = item.monthReference.split("-");
          const itemDate = new Date(parseInt(year), parseInt(month) - 1, 1);

          return { ...item, parsedDate: itemDate };
        });

        const sortedAndFilteredResponse = dataWithDates
          .filter((item) => item.parsedDate >= previousMonth)
          .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

        const [firstMonth, lastMonth] = formatMinMaxDate(
          sortedAndFilteredResponse
        );
        setPeriod([firstMonth, lastMonth]);
      } catch (error) {
        snackbar({
          message: (error as AppError).message,
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [supplyService, snackbar]
  );

  const handleChangeSelectedCompanies = useCallback(
    (event: string[]) => {
      if (event?.length > 0) {
        const selectedContracts = contracts.filter((ele) =>
          event.includes(ele.id)
        );
        setCodeContract(event);
        setCodeMods(_.uniq(selectedContracts.map((ele) => ele.mod)).join(","));
        setCodeProducts(
          _.uniq(selectedContracts.map((ele) => ele.productId)).join(",")
        );
      }
    },
    [setCodeContract, contracts]
  );

  const getContracts = useCallback(
    async (id: string, monthReference: string) => {
      try {
        setCodeContract([]);
        const responseContracts =
          await supplyService.getAllContractsInInterval?.({
            id: id,
            from: monthReference,
            to: monthReference,
          });

        setContracts(responseContracts);
      } catch (error) {
        snackbar({
          message: (error as AppError).message,
          variant: "error",
        });
      }
    },
    []
  );

  useEffect(() => {
    if (codeDistributor && codePeriod) {
      getContracts(codeDistributor, codePeriod);
    }
  }, [codePeriod]);

  const handleChange = useCallback(() => {
    onButtonClearFilter(false);

    const query: IQueryBalanceProps = {
      ...(codeSubsidiary && { subsidiaryId: codeSubsidiary }),
      ...(codePeriod && { monthReference: codePeriod }),
      ...(selectedDistributorsStoraged?.length !== 0 && {
        productId: codeProducts as string,
        mod: codeMods as string,
        customerCompanyId: selectedDistributorsStoraged.join(","),
      }),
    };
    onButtonChange(query);
  }, [codeDistributor, codePeriod, codeMods, codeProducts, codeSubsidiary]);

  const handleClearFilter = useCallback(() => {
    setCodePeriod(null);
    setCodeProducts([]);
    setCodeSubsidiary(undefined);
    setCodeMods([]);
    setCodeContract([]);
    onButtonClearFilter(true);
  }, [onButtonClearFilter]);

  useEffect(() => {
    if (selectedDistributorsStoraged.length !== 0) {
      setCodeDistributor(selectedDistributorsStoraged.join(","));
      getPeriodData(selectedDistributorsStoraged.join(","));
    }
  }, [selectedDistributorsStoraged]);

  const isAllSelected = useMemo(
    () =>
      codePeriod !== null &&
      codeContract.length !== 0 &&
      selectedDistributorsStoraged?.length !== 0,
    [codePeriod, codeContract, selectedDistributorsStoraged]
  );

  const disabled = useMemo(() => {
    return !codeDistributor || !period[0] || !period[1] || isLoading;
  }, [codeDistributor, period, isLoading]);

  const hasError = useMemo(() => {
    return (!period[0] || !period[1]) && !isLoading && !!codeDistributor;
  }, [period, isLoading]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <MultipleSelectDistributors
          maxWidth={1200}
          label={"Selecione o cliente"}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SelectSubsidiaries
          label={"Filial"}
          valueSubsidiary={subsidiaries.find(
            (item) => item.id === codeSubsidiary
          )}
          onChangeValue={(selectedOption) => {
            if (selectedOption) {
              setCodeSubsidiary(selectedOption.id);
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MonthYearPicker
          fullWidth={true}
          label="Período"
          minDate={period[0]}
          maxDate={period[1]}
          value={codePeriod ? new Date(codePeriod) : null}
          onChange={(date) => {
            if (!date) return null;
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            setCodePeriod(`${month.toString().padStart(2, "0")}-${year}`);
          }}
          disabled={disabled}
          hasError={hasError}
          messageError={
            hasError ? "Não há períodos disponíveis para o cliente" : ""
          }
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MultipleSelectCheckbox
          maxWidth={1200}
          minWidth={100}
          label="Contratos"
          isDisabled={!codePeriod}
          options={convertToOptionsList(contracts, "id", "label")}
          value={codeContract as string[]}
          onChange={handleChangeSelectedCompanies}
        />
      </Grid>

      <Grid item xs={12} container justifyContent="flex-end" gap={1}>
        <CustomButton
          title="Limpar filtros"
          variant={CustomButtonVariant.OUTLINED}
          onClick={handleClearFilter}
        />

        <CustomButton
          title="Consultar"
          variant={
            isAllSelected
              ? CustomButtonVariant.CONTAINED
              : CustomButtonVariant.DISABLED
          }
          onClick={handleChange}
          disabled={!isAllSelected}
        />
      </Grid>
    </Grid>
  );
};

export default BalanceFilterTableSection;
